import { logger } from '@/lib/logger';
import {
  addDays,
  addMonths,
  format,
  isAfter,
  set,
  getDay,
} from 'date-fns';

export const SCHEDULE_OPTIONS = [
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'biweekly', label: 'Every 2 Weeks' },
  { id: 'monthly', label: 'Every Month' },
];

export const DAYS_OPTIONS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const MONTHLY_OPTIONS = Array.from({ length: 31 }, (_, i) => (i + 1).toString());

export const CRON_PARTS_EXPECTED_LENGTH = 5;

export const DEFAULT_SCHEDULE_TIME = '09:00';

interface Schedule {
    id: string;
    schedule?: {
      cron?: string;
      timezone?: string;
    };
  }

/**
 * Creates a date with specified time, defaulting to today
 */
const createTimeDate = (hour: string, minute: string, baseDate = new Date()): Date => {
  return set(baseDate, {
    hours: parseInt(hour),
    minutes: parseInt(minute),
    seconds: 0,
    milliseconds: 0
  });
};

/**
 * Checks if current time has passed the target time today
 */
const hasTimePassedToday = (now: Date, targetHour: string, targetMinute: string): boolean => {
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const targetMinutes = parseInt(targetHour) * 60 + parseInt(targetMinute);
  return nowMinutes >= targetMinutes;
};

/**
 * Calculate next run date for weekly schedules
 */
const calculateWeeklyNextRun = (now: Date, hour: string, minute: string, dayOfWeek: string): Date => {
  const targetDay = parseInt(dayOfWeek);
  const currentDay = getDay(now);

  // Calculate days until target day (0-6)
  const daysUntilTarget = (targetDay - currentDay + 7) % 7;

  // If target day is today but time has passed, schedule for next week
  const shouldScheduleNextWeek = daysUntilTarget === 0 && hasTimePassedToday(now, hour, minute);
  const daysToAdd = shouldScheduleNextWeek ? 7 : daysUntilTarget;

  return createTimeDate(hour, minute, addDays(now, daysToAdd));
};

/**
 * Calculate next run date for monthly schedules
 */
const calculateMonthlyNextRun = (now: Date, hour: string, minute: string, dayOfMonth: string): Date => {
  const targetDates = dayOfMonth.split(',').map(Number);
  const firstDate = Math.min(...targetDates);

  // Find next available date in current month
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  for (const date of targetDates.sort((a, b) => a - b)) {
    const candidate = set(now, {
      year: currentYear,
      month: currentMonth,
      date,
      hours: parseInt(hour),
      minutes: parseInt(minute),
      seconds: 0,
      milliseconds: 0
    });
    if (isAfter(candidate, now)) {
      return candidate;
    }
  }

  // If no dates available this month, use first date of next month
  return set(addMonths(now, 1), {
    date: firstDate,
    hours: parseInt(hour),
    minutes: parseInt(minute),
    seconds: 0,
    milliseconds: 0
  });
};

/**
 * Calculate the next run date based on cron expression parts
 */
export const calculateNextRunDate = (cronParts: string[]): Date | null => {
  if (cronParts.length !== CRON_PARTS_EXPECTED_LENGTH) return null;

  const [minute, hour, dayOfMonth, , dayOfWeek] = cronParts;
  const now = new Date();

  // Weekly schedule: specific day of week, any day of month
  if (dayOfWeek !== '*' && dayOfMonth === '*') {
    return calculateWeeklyNextRun(now, hour, minute, dayOfWeek);
  }

  // Monthly schedule: specific day(s) of month, any day of week
  if (dayOfWeek === '*' && dayOfMonth !== '*') {
    return calculateMonthlyNextRun(now, hour, minute, dayOfMonth);
  }

  // Biweekly schedule: both day of week and day of month specified
  if (dayOfWeek !== '*' && dayOfMonth !== '*') {
    // For biweekly, we primarily use the day of week
    return calculateWeeklyNextRun(now, hour, minute, dayOfWeek);
  }

  // Default: run at specified time today or tomorrow
  const nextRun = createTimeDate(hour, minute, now);
  return isAfter(nextRun, now) ? nextRun : addDays(nextRun, 1);
};

/**
 * Parse cron expression into schedule configuration
 */
export const parseScheduleFromCron = (cronExpression?: string | null) => {
  if (!cronExpression) return null;

  const cronParts = cronExpression.split(' ');
  if (cronParts.length !== CRON_PARTS_EXPECTED_LENGTH) return null;

  try {
    const [minute, hour, dayOfMonth, , dayOfWeek] = cronParts;

    // Determine schedule type
    let scheduleType: string | null = null;
    let day: string | null = null;

    if (dayOfWeek === '*' && dayOfMonth === '*') {
      scheduleType = 'daily';
      day = null;
    } else if (dayOfWeek !== '*' && dayOfMonth === '*') {
      // Weekly: specific day of week
      scheduleType = 'weekly';
      day = dayOfWeek;
    } else if (dayOfWeek === '*' && dayOfMonth !== '*') {
      // Monthly or biweekly based on number of dates
      const dates = dayOfMonth.split(',');
      if (dates.length > 1) {
        scheduleType = 'biweekly';
        day = null; // Multiple dates
      } else {
        scheduleType = 'monthly';
        day = dayOfMonth;
      }
    } else if (dayOfWeek !== '*' && dayOfMonth !== '*') {
      // Biweekly: both day of week and day of month
      scheduleType = 'biweekly';
      day = dayOfWeek;
    }

    return {
      schedule: scheduleType,
      day,
      time: `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`,
    };
  } catch (error) {
    logger(error as Error, {
      context: 'Failed to parse cron expression',
      cronExpression
    });
    return null;
  }
};


interface ScheduleConfig {
  schedule: string | null;
  day: string | null;
  time: string;
}

interface NextScheduleInfo {
  nextAnalysis: string | null;
  timezone: string;
}

/**
 * Get schedules and their next runs from fetched schedules
*/
export const getSchedulesAndNextRuns = (
  fetchedSchedules: Schedule[]
): {
  scheduleMap: Record<string, ScheduleConfig>;
  nextScheduleMap: Record<string, NextScheduleInfo>;
} => {
  const scheduleMap: Record<string, ScheduleConfig> = {};
  const nextScheduleMap: Record<string, NextScheduleInfo> = {};

  for (const schedule of fetchedSchedules) {
    const cronExpression = schedule.schedule?.cron;
    const timezone = schedule.schedule?.timezone || 'UTC';

    if (cronExpression) {
      // Parse schedule configuration
      const parsedSchedule = parseScheduleFromCron(cronExpression);
      scheduleMap[schedule.id] = {
        schedule: parsedSchedule?.schedule || null,
        day: parsedSchedule?.day || null,
        time: parsedSchedule?.time || DEFAULT_SCHEDULE_TIME,
      };

      // Calculate next run time
      const cronParts = cronExpression.split(' ');
      let nextAnalysis: string | null = null;

      if (cronParts.length === CRON_PARTS_EXPECTED_LENGTH) {
        const nextRunDate = calculateNextRunDate(cronParts);
        if (nextRunDate) {
          const [, , , , dayOfWeek] = cronParts;

          const dateFormat = dayOfWeek !== '*' ? 'EEEE, MMM d' : 'MMM d';
          nextAnalysis = `Next analysis scheduled at ${format(nextRunDate, 'h:mm a')} on ${format(nextRunDate, dateFormat)}`;
        }
      }

      nextScheduleMap[schedule.id] = { nextAnalysis, timezone };
    } else {
      // No schedule configured
      scheduleMap[schedule.id] = {
        schedule: null,
        day: null,
        time: DEFAULT_SCHEDULE_TIME
      };
      nextScheduleMap[schedule.id] = {
        nextAnalysis: null,
        timezone: 'UTC'
      };
    }
  }

  return { scheduleMap, nextScheduleMap };
};

/**
 * Generates cron expression based on schedule settings
 */
export const generateCronExpression = (instanceSchedule: ScheduleConfig): string => {
    const [hour = '09', minute = '00'] = (instanceSchedule.time || DEFAULT_SCHEDULE_TIME).split(':');
    const { schedule, day } = instanceSchedule;

    if (!schedule) return '';
    if (schedule !== 'daily' && !day) return '';
    switch (schedule) {
      case 'daily':
        return `${minute} ${hour} * * *`;
      case 'weekly':
        return `${minute} ${hour} * * ${day}`;
      case 'biweekly':
        return `${minute} ${hour} 1-7,15-21 * ${day}`;
      case 'monthly':
        return `${minute} ${hour} ${day || '1'} * *`;
      default:
        return '';
    }
  };

